// Pedido fantasma de ajuste — entidade própria pra rastrear retornos
// pós-venda sem mexer no pedido original entregue.
//
// Modelo simplificado:
//   • Original fica intocado em ENTREGUE.
//   • Sistema cria um pedido fantasma novo com:
//       - numero               = `AJ-${orig.numero}` (visual)
//       - tipoAjuste           = true (flag pra filtrar de métricas)
//       - pedidoOriginalId/Numero (link)
//       - motivoAjuste, observacaoAjuste
//       - dataAbertura
//       - status               = CADASTRADO (segue o Kanban padrão)
//   • O fluxo é o MESMO de qualquer pedido: CADASTRADO → FABRICANDO →
//     EXPEDICAO → ROTA → ENTREGUE. A identidade visual (AJ-, ícone 🔧,
//     borda lateral amarela) faz o gerente saber que é ajuste.
//   • Filtros de métrica (Faturamento, A receber) ignoram `tipoAjuste`.

export const MOTIVOS_AJUSTE = [
  { id: 'dano_transporte',  label: 'Dano no transporte' },
  { id: 'dimensao_errada',  label: 'Dimensão errada' },
  { id: 'cliente_reclamou', label: 'Cliente reclamou' },
  { id: 'qualidade',        label: 'Problema de qualidade' },
  { id: 'montagem',         label: 'Erro na montagem' },
  { id: 'outro',            label: 'Outro motivo' },
]

export function labelMotivo(id) {
  return MOTIVOS_AJUSTE.find(m => m.id === id)?.label || id || '—'
}

export function hojeYmd() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

// ID local pro pedido fantasma — não tenta ser globalmente único, só
// suficiente pra esse browser/cliente.
function novoIdAjuste() {
  const ts = Date.now().toString(36)
  const rnd = Math.random().toString(36).slice(2, 6)
  return `aj_${ts}_${rnd}`
}

// Número visual do ajuste — prefixa "AJ-" no número original.
function numeroAjuste(orig) {
  const base = orig.numero || orig.numeroErp || orig.id || 'sem-num'
  // Se já tinha sufixo de ajuste, incrementa (raro, mas evita colisão)
  const m = String(base).match(/^AJ(\d+)?-(.+)$/)
  if (m) {
    const n = (Number(m[1] || 1) + 1)
    return `AJ${n}-${m[2]}`
  }
  return `AJ-${base}`
}

// Cria o pedido fantasma a partir do original. Copia cliente, móveis selecionados,
// começa em status CADASTRADO (Kanban padrão) e zera valor financeiro.
//
// moveisSelecionados: subset dos móveis do orig.moveis (escolhidos no modal).
//   Se não passado, copia TODOS (retrocompatibilidade) — mas o uso correto
//   agora é sempre passar o subset escolhido pelo gerente.
export function criarPedidoAjuste(orig, { motivo, observacao, prazo, moveisSelecionados }) {
  const agoraIso = new Date().toISOString()
  const moveis = Array.isArray(moveisSelecionados) && moveisSelecionados.length > 0
    ? moveisSelecionados.map(m => ({ ...m }))
    : (Array.isArray(orig.moveis) ? orig.moveis.map(m => ({ ...m })) : [])
  return {
    id:                    novoIdAjuste(),
    numero:                numeroAjuste(orig),

    // ── Marcadores de ajuste ─────────────────────────────────────────
    tipoAjuste:            true,
    pedidoOriginalId:      orig.id,
    pedidoOriginalNumero:  orig.numero,
    motivoAjuste:          motivo,
    observacaoAjuste:      observacao || '',
    dataAbertura:          agoraIso,

    // ── Campos de pedido normal (fluxo Kanban padrão) ───────────────
    status:                'CADASTRADO',
    prazo:                 prazo || hojeYmd(),
    criadoEm:              agoraIso,
    cliente:               { ...(orig.cliente || {}) },
    moveis,
    itens:                 Array.isArray(orig.itens)  ? orig.itens.map(i => ({ ...i }))  : [],
    valorTotal:            0,
    valorPendenteEntrega:  0,
    vendedora:             orig.vendedora || '',
    formaPagamento:        '',

    // ── Logística ───────────────────────────────────────────────────
    horarioEntrega:        '',
    confirmacaoEntrega:    '',
    enderecoEntrega:       '',
    observacoesLogistica:  '',
    atencao:               false,
    prazoOriginal:         '',
    pagoEntrega:           false,
    resumo:                `🔧 AJUSTE de #${orig.numero || ''} — ${labelMotivo(motivo)}${observacao ? ': ' + observacao : ''}`,
  }
}

export function diasDoAjuste(pedidoFantasma) {
  if (!pedidoFantasma?.dataAbertura) return 0
  const ini = new Date(pedidoFantasma.dataAbertura).getTime()
  const fim = pedidoFantasma.status === 'ENTREGUE' && pedidoFantasma.entregueEm
    ? new Date(pedidoFantasma.entregueEm).getTime()
    : Date.now()
  return Math.floor((fim - ini) / (1000 * 60 * 60 * 24))
}
